import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { useAuthStore } from '@/state/AuthStore'
import { useToastStore } from '@/state/ToastStore'
import axios from '@/utils/axios'
import { ref } from 'vue'
import { useStorage } from '@vueuse/core'

export function useNotificationStore() {
  const authStore = useAuthStore()
  const userId = authStore.user?.id
  const queryClient = useQueryClient()
  const toastStore = useToastStore()
  const isInitialized = ref(false)
  const shownNotifications = useStorage('shownNotificationsIds', [])

  const showNotification = (notification) => {
    if (shownNotifications.value.includes(notification.id)) {
      return
    }
    shownNotifications.value.push(notification.id)

    if (notification.priority == 'mention') {
      toastStore.addToast({
        title: notification.title,
        body: notification.body,
        link: `/leads/${notification?.linked_object.id}`,
        timer: false
      })
    } else {
      const toastId = toastStore.addToast({
        title: notification.title,
        body: notification.body,
        button: {
          text: 'Mark as Read',
          action: () => markToastAsRead(notification.id, toastId)
        },
        timer: false
      })
    }
  }

  function markToastAsRead(notificationId, toastId) {
    toastStore.closeToast(toastId)
    markAsRead(notificationId)
  }

  const oldNotifications = ref([])
  async function fetchNotifications() {
    const res = await axios.get('/api/notifications/')

    const newNotifications = res.data.filter(
      (notification) =>
        !oldNotifications.value.some((old) => old.id === notification.id) &&
        !shownNotifications.value.includes(notification.id)
    )

    newNotifications.forEach(showNotification)
    oldNotifications.value = res.data
    return res.data
  }

  const {
    data: unreadNotifications,
    isLoading: unreadIsLoading,
    refetch
  } = useQuery({
    queryFn: fetchNotifications,
    queryKey: ['unreadNotifications'],
    enabled: () => isInitialized.value && !!authStore.domain,
    refetchInterval: 15000,
    refetchIntervalInBackground: false,
    retry: 1
  })

  async function setupNotificationStore() {
    try {
      await refetch()
      isInitialized.value = true
    } catch (err) {
      toastStore.addToast({
        title: 'Error Initializing Notification System',
        body: err.message || err,
        theme: 'danger'
      })
    }
  }

  const { data: notificationHistory, isLoading: historyIsLoading } = useQuery({
    queryKey: ['notificationHistory'],
    queryFn: async () => {
      const res = await axios.get('/api/notifications/history/')
      return res.data
    },
    enabled: !!userId
  })

  const { mutate: markAsRead, isPending: markAsReadIsPending } = useMutation({
    mutationFn: async (id) => {
      const res = await axios.post('/api/notifications/mark-as-read/', { ids: [id] })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['unreadNotifications'])
      queryClient.invalidateQueries(['notificationHistory'])
    },
    onError: (err) => {
      toastStore.addToast({
        title: 'Error marking notification as read',
        body: err.response.data.error || err,
        theme: 'danger'
      })
    }
  })

  const { mutate: markAllAsRead, isPending: markAllAsReadIsPending } = useMutation({
    mutationFn: async () => {
      const unreadIds =
        notificationHistory.value
          ?.filter((notification) => notification.is_unread)
          .map((notification) => notification.id) || []
      if (unreadIds.length === 0) return
      const res = await axios.post('/api/notifications/mark-as-read/', { ids: unreadIds })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['unreadNotifications'])
      queryClient.invalidateQueries(['notificationHistory'])
      toastStore.toastStack.slice().forEach((toast) => {
        toastStore.closeToast(toast.id)
      })
    },
    onError: (err) => {
      toastStore.addToast({
        title: 'Error marking all notifications as read',
        body: err.response.data.error || err,
        theme: 'danger'
      })
    }
  })

  return {
    setupNotificationStore,
    unreadNotifications,
    unreadIsLoading,
    notificationHistory,
    historyIsLoading,
    markAsRead,
    markAsReadIsPending,
    markAllAsRead,
    markAllAsReadIsPending
  }
}
